<template>
    <div v-if="objectDetails && activeObject && !onAir" class="ovt__legend details"
        :class="{ 'with-config': configPanelOpen, 'fvt-legend': tool === 'fvt' }">
        <div class="ovt__legend--label">{{ $t.objectDetails }}</div>

        <!-- clickable button -->
        <button v-if="activeObject._useEphemTable && closeApproachDetected(activeObject.distance.date) === 'clickable'" class="cancel"
            @click="openFBVTcloseApproach(activeObject._obj.name)">
            <i class="icon-alert-triangle" />
            <span>{{ $t.closeEncounterDetected.replace('{mainBody}', closestMainBody) }}</span>
        </button>

        <!-- non-clickable yellow button -->
        <button v-else-if="activeObject._useEphemTable && closeApproachDetected(activeObject.distance.date) === 'nonclickable'"
            class="cancel-redirection-to-fvt" style="cursor: default;" disabled>
            <i class="icon-alert-triangle" />
            <span>{{ $t.closeEncounterDetectedWithNoFVTcounterpart.replace('{mainBody}', closestMainBody).replace('{minThreshold}', minThreshold) }}</span>
        </button>

        <div class="d-table">
            <div class="d-table-row">
                <div class="d-table-cell">{{ $t.designator }}:</div>
                <div class="d-table-cell">{{ activeObject._options.name }}</div>
            </div>
            <div class="d-table-row">
                <div class="d-table-cell">{{ $t.diameter }}:</div>
                <div class="d-table-cell">{{ activeObject._options.diam ? activeObject._options.diam.toFixed(1) : '-' }} m
                </div>
            </div>
            <div class="d-table-row">
                <div class="d-table-cell">{{ $t.absoluteMagnitude }}:</div>
                <div class="d-table-cell">{{ activeObject._options.mag ? activeObject._options.mag.toFixed(1) : '-' }} mag
                </div>
            </div>
            <div class="d-table-row">
                <div class="d-table-cell">{{ $t.systemTimeMJD }}:</div>
                <div class="d-table-cell">{{ getMJD(activeObject.distance.date) }}</div>
            </div>
            <div v-if="tool === 'ovt' || tool === 'sovt'" class="d-table-row">
                <div class="d-table-cell">{{ $t.systemTime }}:</div>
                <div class="d-table-cell">{{ getDateSimulationTime(activeObject.distance.simulationTime) }}</div>
            </div>
            <div v-if="tool === 'fvt'" class="d-table-row">
                <div class="d-table-cell">{{ $t.systemTime }} UTC:</div>
                <div class="d-table-cell">{{ getDateSimulationTime(activeObject.distance.date) }}</div>
            </div>
            <div class="d-table-row">
                <div class="d-table-cell">{{ $t.distanceToSun }}:</div>
                <div class="d-table-cell">{{ isAfterImpact(activeObject) ? '-' : activeObject.distance.sun }} {{ $t.au }}</div>
            </div>
            <div class="d-table-row">
                <div class="d-table-cell">{{ $t.distanceToMainBodyCentre.replace('{mainBody}', mainBodyName()) }}:</div>
                <div class="d-table-cell">{{ isAfterImpact(activeObject) ? '-' : activeObject.distance.earth }} {{ $t.au }}</div>
            </div>
            <div v-if="(tool === 'ovt' || tool === 'sovt')" class="d-table-row">
                <div class="d-table-cell">{{ $t.orbitPropagation }}:</div>
                <div class="d-table-cell">{{ orbitPropagation() }}</div>
            </div>
            <div v-if="(tool === 'ovt' || tool === 'sovt') && activeObject._useEphemTable" class="d-table-row">
                <div class="d-table-cell">{{ $t.orbitReliable }}:</div>
                <div class="d-table-cell">{{ orbitReliable(activeObject.distance.date) }}</div>
            </div>
            <div v-if="tool === 'fvt' && activeObject && activeObject._options.shape" class="d-table-row">
                <div class="d-table-cell">{{ $t.distanceToMainBodyCentre.replace('{mainBody}', mainBodyName()) }}:</div>
                <div class="d-table-cell">{{ (activeObject.distance.earth * 149597870.7).toFixed(2) }} km </div>
            </div>
            <div v-if="tool === 'fvt' && activeObject && activeObject._options.shape" class="d-table-row">
                <div class="d-table-cell">{{ $t.modelSource }}:</div>
                <div v-if="activeObject._options.shape.modelSource === 'generic'" class="d-table-cell">{{
                    activeObject._options.shape.modelSource }}</div>
                <div v-else class="d-table-cell">
                    <button class="link" @click="goToModelSource(activeObject)"><span>{{
                        activeObject._options.shape.modelSource }}</span><i class="icon-external-link"></i></button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VisualisationService from '@/services/visualisation.service'
import UtilsService from '@/services/utils.service'
import moment from 'moment'
import TimeFrameService from '@/services/timeframe.service'
import PopupService from '@/services/popup.service'
import SyntheticObjectsService from '@/services/synthetic-objects.service'
import Keys from '@/constants/keys'

export default {
    name: 'VisualisationObjectLegend',
    data() {
        return {
            object: null,
            closestMainBody: null,
        }
    },
    computed: {
        minThreshold() {
            if (!this.closestMainBody) return 'SOI';
            return this.closestMainBody === 'Earth' ? '3 LD' : 'SOI';
        },        
        settings() {
            return VisualisationService.getSettings();
        },
        configPanelOpen() {
            return VisualisationService.getSelectObjectsShow();
        },
        objectDetails() {
            return this.settings && this.settings.objectsSettings.objectDetails.value;
        },
        activeObject() {
            return VisualisationService.getOrbitActiveObject();
        },
        simulationTime() {
            return VisualisationService.getSimulationTime();
        },
        tool() {
            return VisualisationService.getTool();
        },
        orbitLegend() {
            return this.settings && this.settings.objectsSettings.orbitLegend.value;
        },
        onAir() {
            return VisualisationService.getOrbitRecorderOnAir();
        }
    },
    methods: {
        mainBodyName() {
            return VisualisationService.getMainBodyName();
        },
        orbitPropagation() {
            if (this.activeObject._useEphemTable) {
                return this.$t.accurate;
            } else {
                return this.$t.keplerian;
            }
        },
        getDate(date) {
            if (this.tool === 'ovt' || this.tool === 'sovt') {
                return UtilsService.dateToString(date);
            } else {
                return UtilsService.dateToString(date, true);
            }

        },
        getDateSimulationTime(date) {
            if (this.tool === 'ovt' || this.tool === 'sovt') {
                return moment(UtilsService.dateToStringTechnical(date)).format('DD-MM-YYYY');
            } else {
                return moment(UtilsService.dateToStringTechnicalFVTInit(date, true)).format('DD-MM-YYYY HH:mm');
            }
        },
        getMJD(date) {
            if (this.tool === 'ovt' || this.tool === 'sovt') {
                const currentDate = parseInt(UtilsService.dateToJulian(VisualisationService.getCurrentDate()) - 2400000.5);
                return currentDate;
                //return parseInt(UtilsService.dateToJulian(date)) - 2400000;
            } else {
                return (UtilsService.dateToJulian(date) - 2400000.5).toFixed(5);
            }
        },
        orbitReliable(date) {
            const mjd = parseInt(UtilsService.dateToJulian(date)) - 2400000;
            if (this.activeObject._options.uncTable)
                return this.activeObject._options.uncTable.indexOf(mjd) > -1 ? this.$t.no : this.$t.yes;
            else return;
        },

        // returns one of: 'clickable', 'nonclickable', or null
        closeApproachDetected(date) {
            if (this.tool === 'fvt') return false;
            const jd = UtilsService.dateToJulian(date);
            const mjd = parseInt(jd) - 2400000;

            const closeApproaches = this.activeObject._options.closeApproachTable;
            const detectedCloseApproachForFVT_earth = this.activeObject._options.detectedCloseApproachForFVT_earth;
            const detectedCloseApproachForFVT_mercury = this.activeObject._options.detectedCloseApproachForFVT_mercury;
            const detectedCloseApproachForFVT_venus = this.activeObject._options.detectedCloseApproachForFVT_venus;
            const detectedCloseApproachForFVT_mars = this.activeObject._options.detectedCloseApproachForFVT_mars;
            const detectedCloseApproachForFVT_saturn = this.activeObject._options.detectedCloseApproachForFVT_saturn;
            const detectedCloseApproachForFVT_jupiter = this.activeObject._options.detectedCloseApproachForFVT_jupiter;
            const detectedCloseApproachForFVT_uranus = this.activeObject._options.detectedCloseApproachForFVT_uranus;
            const detectedCloseApproachForFVT_neptune = this.activeObject._options.detectedCloseApproachForFVT_neptune;

            if (!closeApproaches || (!detectedCloseApproachForFVT_earth && !detectedCloseApproachForFVT_mercury && !detectedCloseApproachForFVT_venus && !detectedCloseApproachForFVT_mars && !detectedCloseApproachForFVT_saturn && !detectedCloseApproachForFVT_jupiter && !detectedCloseApproachForFVT_uranus && !detectedCloseApproachForFVT_neptune)) {
                return null;
            }

            const index = closeApproaches.indexOf(mjd);

            if (index > -1) {
                this.findClosestMainBody(jd);
                let result = 'nonclickable';
                if (this.closestMainBody === 'Earth' && detectedCloseApproachForFVT_earth[index]) {
                    result = 'clickable';
                } else if (this.closestMainBody === 'Mercury' && detectedCloseApproachForFVT_mercury[index]) {
                    result = 'clickable';
                } else if (this.closestMainBody === 'Venus' && detectedCloseApproachForFVT_venus[index]) {
                    result = 'clickable';
                } else if (this.closestMainBody === 'Mars' && detectedCloseApproachForFVT_mars[index]) {
                    result = 'clickable';
                } else if (this.closestMainBody === 'Saturn' && detectedCloseApproachForFVT_saturn[index]) {
                    result = 'clickable';
                } else if (this.closestMainBody === 'Jupiter' && detectedCloseApproachForFVT_jupiter[index]) {
                    result = 'clickable';
                } else if (this.closestMainBody === 'Uranus' && detectedCloseApproachForFVT_uranus[index]) {
                    result = 'clickable';
                } else if (this.closestMainBody === 'Neptune' && detectedCloseApproachForFVT_neptune[index]) {
                    result = 'clickable';
                }
                return result;
            }

            return null;
        },

        createHref(object) {
            const source = object._options.shape.modelSource;
            const designator = object._options.designator;

            const services = {
                '3d-asteroids': 'https://3d-asteroids.space/asteroids/',
                'damit': 'https://astro.troja.mff.cuni.cz/projects/damit/?q=',
            }

            const url = services[source];
            return url + designator;
        },
        goToModelSource(object) {
            const url = this.createHref(object);
            window.open(url, '_blank').focus();
        },
        findClosestMainBody(date) {
            const vt = window['vt'];
            const distancesToPlanets = [];
            const xyzObject = this.activeObject.getPosition(date);
            vt.planets.map(planet => {
                const xyzBody = planet.getPosition(date);
                distancesToPlanets.push({
                    name: planet._options.name,
                    distance: 1*VisualisationService.distanceToBody(xyzObject, xyzBody, 5)
                })
            })
            this.closestMainBody = distancesToPlanets.sort((a,b) => a.distance - b.distance)[0].name;
        },
        openFBVTcloseApproach(object) {
            const isSyntheticObject = SyntheticObjectsService.isSyntheticObject(this.activeObject._obj.name);
            if (isSyntheticObject) {
                PopupService.show({
                    component: 'PopupInfo',
                    type: 'warning',
                    message: Keys.t.connectionToFbvtIsNotEnabledForUserDefinedObjects,
                });
                return;
            }
            const start = TimeFrameService.getTimeFrame().start;
            const end = TimeFrameService.getTimeFrame().end;
            const designator = object;
            const mainbody = this.closestMainBody.toLowerCase();
            window.open(`/fvt?start=${start}&end=${end}&object=${designator}&mainbody=${mainbody}`, '_blank');
        },
        isAfterImpact(object) {
            if (object && !object.impactDate) {
                return false;
            }
            return (new Date(object.impactDate)).getTime() <= this.simulationTime.getTime();
        }
    },
}
</script>

<style></style>
